/**
 * Automations Database Operations
 *
 * CRUD operations for workflow automation rules.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { handleNotFoundError } from './helpers';
import { logger } from '@/utils/logger';

// Trigger types that can start an automation
export type TriggerType =
  | 'task_created'
  | 'status_changed'
  | 'assignee_changed'
  | 'due_date_changed'
  | 'tag_added'
  | 'tag_removed'
  | 'priority_changed'
  | 'comment_added'
  | 'subtask_completed';

// Condition operators for filtering
export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'is_empty'
  | 'is_not_empty'
  | 'greater_than'
  | 'less_than';

// Fields that can be used in conditions
export type ConditionField =
  | 'status'
  | 'priority'
  | 'assignees'
  | 'tags'
  | 'due_date'
  | 'start_date'
  | 'name'
  | 'story_points';

// Action types that can be executed
export type ActionType =
  | 'change_status'
  | 'set_priority'
  | 'add_assignee'
  | 'remove_assignee'
  | 'add_tag'
  | 'remove_tag'
  | 'set_due_date'
  | 'send_notification'
  | 'create_subtask'
  | 'add_comment';

export interface AutomationCondition {
  field: ConditionField;
  operator: ConditionOperator;
  value: string | number | string[] | null;
}

export interface AutomationAction {
  type: ActionType;
  config: Record<string, unknown>;
}

export interface AutomationTriggerConfig {
  // For status_changed: { from?: string, to?: string }
  // For assignee_changed: { added?: string, removed?: string }
  // For tag_added/removed: { tag?: string }
  [key: string]: unknown;
}

export interface Automation {
  id: string;
  spaceId: string;
  name: string;
  description: string | null;
  enabled: boolean;
  triggerType: TriggerType;
  triggerConfig: AutomationTriggerConfig;
  conditions: AutomationCondition[];
  actions: AutomationAction[];
  runCount: number;
  lastRunAt: string | null;
  lastError: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AutomationLog {
  id: string;
  automationId: string;
  taskId: string | null;
  triggerData: Record<string, unknown>;
  actionsExecuted: AutomationAction[];
  success: boolean;
  errorMessage: string | null;
  executedAt: string;
}

export interface CreateAutomationData {
  spaceId: string;
  name: string;
  description?: string;
  enabled?: boolean;
  triggerType: TriggerType;
  triggerConfig?: AutomationTriggerConfig;
  conditions?: AutomationCondition[];
  actions: AutomationAction[];
  createdBy?: string;
}

export interface UpdateAutomationData {
  name?: string;
  description?: string;
  enabled?: boolean;
  triggerType?: TriggerType;
  triggerConfig?: AutomationTriggerConfig;
  conditions?: AutomationCondition[];
  actions?: AutomationAction[];
}

// Supabase row type for automations table
interface AutomationRow {
  id: string;
  space_id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  trigger_type: TriggerType;
  trigger_config: AutomationTriggerConfig | null;
  conditions: AutomationCondition[] | null;
  actions: AutomationAction[] | null;
  run_count: number | null;
  last_run_at: string | null;
  last_error: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// Supabase row type for automation logs
interface AutomationLogRow {
  id: string;
  automation_id: string;
  task_id: string | null;
  trigger_data: Record<string, unknown> | null;
  actions_executed: AutomationAction[] | null;
  success: boolean;
  error_message: string | null;
  executed_at: string;
}

// Convert from Supabase (snake_case) to Automation (camelCase)
function convertAutomationFromSupabase(row: AutomationRow): Automation {
  return {
    id: row.id,
    spaceId: row.space_id,
    name: row.name,
    description: row.description,
    enabled: row.enabled,
    triggerType: row.trigger_type,
    triggerConfig: row.trigger_config || {},
    conditions: row.conditions || [],
    actions: row.actions || [],
    runCount: row.run_count || 0,
    lastRunAt: row.last_run_at,
    lastError: row.last_error,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function convertLogFromSupabase(row: AutomationLogRow): AutomationLog {
  return {
    id: row.id,
    automationId: row.automation_id,
    taskId: row.task_id,
    triggerData: row.trigger_data || {},
    actionsExecuted: row.actions_executed || [],
    success: row.success,
    errorMessage: row.error_message,
    executedAt: row.executed_at,
  };
}

/**
 * Get all automations for a space
 */
export async function getAutomationsBySpace(spaceId: string): Promise<Automation[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('automations')
      .select('*')
      .eq('space_id', spaceId)
      .order('created_at', { ascending: false })
  );

  if (error) throw error;
  return (data || []).map(convertAutomationFromSupabase);
}

/**
 * Get enabled automations for a space by trigger type
 */
export async function getEnabledAutomations(
  spaceId: string,
  triggerType: TriggerType
): Promise<Automation[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('automations')
      .select('*')
      .eq('space_id', spaceId)
      .eq('trigger_type', triggerType)
      .eq('enabled', true)
  );

  if (error) throw error;
  return (data || []).map(convertAutomationFromSupabase);
}

/**
 * Get a single automation by ID
 */
export async function getAutomation(id: string): Promise<Automation | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('automations')
      .select('*')
      .eq('id', id)
      .single()
  );

  if (error) return handleNotFoundError(error, null);
  return convertAutomationFromSupabase(data);
}

/**
 * Create a new automation
 */
export async function createAutomation(data: CreateAutomationData): Promise<Automation> {
  const insertData = {
    space_id: data.spaceId,
    name: data.name,
    description: data.description || null,
    enabled: data.enabled ?? true,
    trigger_type: data.triggerType,
    trigger_config: data.triggerConfig || {},
    conditions: data.conditions || [],
    actions: data.actions,
    created_by: data.createdBy || null,
  };

  const { data: created, error } = await withRetry(() =>
    supabase
      .from('automations')
      .insert(insertData)
      .select()
      .single()
  );

  if (error || !created) throw error || new Error('Failed to create automation');
  return convertAutomationFromSupabase(created);
}

/**
 * Update an automation
 */
export async function updateAutomation(
  id: string,
  updates: UpdateAutomationData
): Promise<Automation> {
  const updatePayload: Record<string, unknown> = {};
  if (updates.name !== undefined) updatePayload.name = updates.name;
  if (updates.description !== undefined) updatePayload.description = updates.description;
  if (updates.enabled !== undefined) updatePayload.enabled = updates.enabled;
  if (updates.triggerType !== undefined) updatePayload.trigger_type = updates.triggerType;
  if (updates.triggerConfig !== undefined) updatePayload.trigger_config = updates.triggerConfig;
  if (updates.conditions !== undefined) updatePayload.conditions = updates.conditions;
  if (updates.actions !== undefined) updatePayload.actions = updates.actions;

  const { data, error } = await withRetry(() =>
    supabase
      .from('automations')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single()
  );

  if (error || !data) throw error || new Error('Failed to update automation');
  return convertAutomationFromSupabase(data);
}

/**
 * Delete an automation
 */
export async function deleteAutomation(id: string): Promise<boolean> {
  const { error } = await withRetry(() => supabase.from('automations').delete().eq('id', id));

  if (error) throw error;
  return true;
}

/**
 * Toggle automation enabled state
 */
export async function toggleAutomation(id: string): Promise<Automation> {
  // First get current state
  const automation = await getAutomation(id);
  if (!automation) throw new Error('Automation not found');

  return updateAutomation(id, { enabled: !automation.enabled });
}

/**
 * Log an automation execution
 */
export async function logAutomationExecution(data: {
  automationId: string;
  taskId?: string;
  triggerData: Record<string, unknown>;
  actionsExecuted: AutomationAction[];
  success: boolean;
  errorMessage?: string;
}): Promise<AutomationLog> {
  const insertData = {
    automation_id: data.automationId,
    task_id: data.taskId || null,
    trigger_data: data.triggerData,
    actions_executed: data.actionsExecuted,
    success: data.success,
    error_message: data.errorMessage || null,
  };

  const { data: log, error } = await withRetry(() =>
    supabase
      .from('automation_logs')
      .insert(insertData)
      .select()
      .single()
  );

  if (error || !log) throw error || new Error('Failed to log automation execution');

  // Update automation run count and last run time
  // First get current run_count, then increment
  const { data: automation } = await withRetry(() =>
    supabase
      .from('automations')
      .select('run_count')
      .eq('id', data.automationId)
      .single()
  );

  await withRetry(() =>
    supabase
      .from('automations')
      .update({
        run_count: (automation?.run_count || 0) + 1,
        last_run_at: new Date().toISOString(),
        last_error: data.success ? null : data.errorMessage,
      })
      .eq('id', data.automationId)
  );

  return convertLogFromSupabase(log);
}

/**
 * Get execution logs for an automation
 */
export async function getAutomationLogs(
  automationId: string,
  limit: number = 50
): Promise<AutomationLog[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('automation_logs')
      .select('*')
      .eq('automation_id', automationId)
      .order('executed_at', { ascending: false })
      .limit(limit)
  );

  if (error) throw error;
  return (data || []).map(convertLogFromSupabase);
}

/**
 * Evaluate if conditions match a task
 */
export function evaluateConditions(
  conditions: AutomationCondition[],
  task: Record<string, unknown>
): boolean {
  if (conditions.length === 0) return true;

  return conditions.every((condition) => {
    const taskValue = task[condition.field];

    switch (condition.operator) {
      case 'equals':
        return taskValue === condition.value;
      case 'not_equals':
        return taskValue !== condition.value;
      case 'contains':
        if (Array.isArray(taskValue)) {
          return taskValue.includes(condition.value);
        }
        return String(taskValue).toLowerCase().includes(String(condition.value).toLowerCase());
      case 'not_contains':
        if (Array.isArray(taskValue)) {
          return !taskValue.includes(condition.value);
        }
        return !String(taskValue).toLowerCase().includes(String(condition.value).toLowerCase());
      case 'is_empty':
        return taskValue === null || taskValue === '' || (Array.isArray(taskValue) && taskValue.length === 0);
      case 'is_not_empty':
        return taskValue !== null && taskValue !== '' && (!Array.isArray(taskValue) || taskValue.length > 0);
      case 'greater_than':
        return Number(taskValue) > Number(condition.value);
      case 'less_than':
        return Number(taskValue) < Number(condition.value);
      default:
        return true;
    }
  });
}

/**
 * Execute actions on a task.
 * Applies each action by updating the task row directly.
 */
export async function executeActions(
  taskId: string,
  actions: AutomationAction[],
): Promise<void> {
  for (const action of actions) {
    switch (action.type) {
      case 'change_status': {
        const status = action.config.status as string | undefined;
        if (status) {
          await withRetry(() =>
            supabase.from('tasks').update({ status }).eq('id', taskId),
          );
        }
        break;
      }
      case 'set_priority': {
        const priority = (action.config.priority as string | null) ?? null;
        await withRetry(() =>
          supabase.from('tasks').update({ priority }).eq('id', taskId),
        );
        break;
      }
      case 'add_assignee': {
        const assignee = action.config.assignee as string | undefined;
        if (assignee) {
          const { data: task } = await withRetry(() =>
            supabase.from('tasks').select('assignees').eq('id', taskId).single(),
          );
          const current: string[] = (task?.assignees as string[]) || [];
          if (!current.includes(assignee)) {
            await withRetry(() =>
              supabase.from('tasks').update({ assignees: [...current, assignee] }).eq('id', taskId),
            );
          }
        }
        break;
      }
      case 'remove_assignee': {
        const assignee = action.config.assignee as string | undefined;
        if (assignee) {
          const { data: task } = await withRetry(() =>
            supabase.from('tasks').select('assignees').eq('id', taskId).single(),
          );
          const current: string[] = (task?.assignees as string[]) || [];
          await withRetry(() =>
            supabase.from('tasks').update({ assignees: current.filter(a => a !== assignee) }).eq('id', taskId),
          );
        }
        break;
      }
      case 'add_tag': {
        const tag = action.config.tag as string | undefined;
        if (tag) {
          const { data: task } = await withRetry(() =>
            supabase.from('tasks').select('tags').eq('id', taskId).single(),
          );
          const current: string[] = (task?.tags as string[]) || [];
          if (!current.includes(tag)) {
            await withRetry(() =>
              supabase.from('tasks').update({ tags: [...current, tag] }).eq('id', taskId),
            );
          }
        }
        break;
      }
      case 'remove_tag': {
        const tag = action.config.tag as string | undefined;
        if (tag) {
          const { data: task } = await withRetry(() =>
            supabase.from('tasks').select('tags').eq('id', taskId).single(),
          );
          const current: string[] = (task?.tags as string[]) || [];
          await withRetry(() =>
            supabase.from('tasks').update({ tags: current.filter(t => t !== tag) }).eq('id', taskId),
          );
        }
        break;
      }
      case 'set_due_date': {
        const dueDate = action.config.due_date as string | number | null | undefined;
        const dueDateMs = dueDate ? (typeof dueDate === 'number' ? dueDate : new Date(dueDate).getTime()) : null;
        await withRetry(() =>
          supabase.from('tasks').update({ due_date: dueDateMs }).eq('id', taskId),
        );
        break;
      }
      case 'send_notification': {
        const message = action.config.message as string | undefined;
        const userIds = action.config.user_ids as string[] | undefined;
        if (message && userIds && userIds.length > 0) {
          const rows = userIds.map(uid => ({
            user_id: uid,
            type: 'task_updated',
            task_id: taskId,
            message,
          }));
          await withRetry(() => supabase.from('notifications').insert(rows));
        }
        break;
      }
      case 'create_subtask': {
        const name = action.config.name as string | undefined;
        if (name) {
          const { data: parent } = await withRetry(() =>
            supabase.from('tasks').select('space_id, status').eq('id', taskId).single(),
          );
          await withRetry(() =>
            supabase.from('tasks').insert({
              name,
              parent_id: taskId,
              space_id: parent?.space_id,
              status: 'open',
            }),
          );
        }
        break;
      }
      case 'add_comment': {
        const text = action.config.text as string | undefined;
        const author = action.config.author as string | undefined;
        if (text) {
          const { data: task } = await withRetry(() =>
            supabase.from('tasks').select('comments').eq('id', taskId).single(),
          );
          const comments: unknown[] = (task?.comments as unknown[]) || [];
          const newComment = {
            id: crypto.randomUUID(),
            content: text,
            authorId: author || 'system',
            createdAt: new Date().toISOString(),
            mentions: [],
          };
          await withRetry(() =>
            supabase.from('tasks').update({ comments: [...comments, newComment] }).eq('id', taskId),
          );
        }
        break;
      }
    }
  }
}

/**
 * Run all enabled automations for a given trigger on a task.
 * Called fire-and-forget from DatabaseContext after task mutations.
 */
export async function runAutomationsForTrigger(
  spaceId: string,
  triggerType: TriggerType,
  taskId: string,
  taskData: Record<string, unknown>,
  triggerData: Record<string, unknown> = {},
): Promise<void> {
  try {
    const automations = await getEnabledAutomations(spaceId, triggerType);
    if (automations.length === 0) return;

    for (const automation of automations) {
      try {
        // Check trigger config match (e.g., status_changed from/to)
        if (!matchesTriggerConfig(automation, triggerData)) continue;

        // Evaluate conditions against task data
        if (!evaluateConditions(automation.conditions, taskData)) continue;

        // Execute actions
        await executeActions(taskId, automation.actions);

        // Log success
        await logAutomationExecution({
          automationId: automation.id,
          taskId,
          triggerData,
          actionsExecuted: automation.actions,
          success: true,
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        logger.warn(`Automation "${automation.name}" failed:`, err);
        await logAutomationExecution({
          automationId: automation.id,
          taskId,
          triggerData,
          actionsExecuted: automation.actions,
          success: false,
          errorMessage,
        }).catch(() => {/* ignore logging failure */});
      }
    }
  } catch (err) {
    logger.warn('Failed to run automations for trigger:', triggerType, err);
  }
}

/**
 * Check if trigger data matches the automation's trigger config.
 * For example, a status_changed automation may only fire when going to a specific status.
 */
function matchesTriggerConfig(
  automation: Automation,
  triggerData: Record<string, unknown>,
): boolean {
  const config = automation.triggerConfig;
  if (!config || Object.keys(config).length === 0) return true;

  // status_changed: optional from/to filters
  if (automation.triggerType === 'status_changed') {
    if (config.from && triggerData.oldStatus !== config.from) return false;
    if (config.to && triggerData.newStatus !== config.to) return false;
  }

  // priority_changed: optional from/to filters
  if (automation.triggerType === 'priority_changed') {
    if (config.from && triggerData.oldPriority !== config.from) return false;
    if (config.to && triggerData.newPriority !== config.to) return false;
  }

  // tag_added/tag_removed: optional tag filter
  if ((automation.triggerType === 'tag_added' || automation.triggerType === 'tag_removed') && config.tag) {
    if (triggerData.tag !== config.tag) return false;
  }

  // assignee_changed: optional added/removed filter
  if (automation.triggerType === 'assignee_changed') {
    if (config.added && triggerData.addedAssignee !== config.added) return false;
    if (config.removed && triggerData.removedAssignee !== config.removed) return false;
  }

  return true;
}

// Trigger type labels for UI
export const TRIGGER_TYPE_LABELS: Record<TriggerType, string> = {
  task_created: 'Task creato',
  status_changed: 'Status cambiato',
  assignee_changed: 'Assegnatario cambiato',
  due_date_changed: 'Scadenza cambiata',
  tag_added: 'Tag aggiunto',
  tag_removed: 'Tag rimosso',
  priority_changed: 'Priorità cambiata',
  comment_added: 'Commento aggiunto',
  subtask_completed: 'Subtask completato',
};

// Action type labels for UI
export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  change_status: 'Cambia status',
  set_priority: 'Imposta priorità',
  add_assignee: 'Aggiungi assegnatario',
  remove_assignee: 'Rimuovi assegnatario',
  add_tag: 'Aggiungi tag',
  remove_tag: 'Rimuovi tag',
  set_due_date: 'Imposta scadenza',
  send_notification: 'Invia notifica',
  create_subtask: 'Crea subtask',
  add_comment: 'Aggiungi commento',
};

// Condition operator labels for UI
export const CONDITION_OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'è uguale a',
  not_equals: 'non è uguale a',
  contains: 'contiene',
  not_contains: 'non contiene',
  is_empty: 'è vuoto',
  is_not_empty: 'non è vuoto',
  greater_than: 'è maggiore di',
  less_than: 'è minore di',
};

// Condition field labels for UI
export const CONDITION_FIELD_LABELS: Record<ConditionField, string> = {
  status: 'Status',
  priority: 'Priorità',
  assignees: 'Assegnatari',
  tags: 'Tag',
  due_date: 'Scadenza',
  start_date: 'Data inizio',
  name: 'Nome',
  story_points: 'Story points',
};
